import { Box, Button, makeStyles, Typography } from "@material-ui/core";
import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { logout } from "../../model/oidc/actions";
import { Me } from "../../types/api-schema";
import { useAppDispatch, useAppSelector } from "../../utils/customHooks";

export const WIZARD_PATHNAME = "/wizard";
export const BILLING_PATHNAME = "/billing";

const useStyles = makeStyles(() => {
  return {
    root: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      gap: 24,
      justifyContent: "center",
      minHeight: "100vh",
      padding: 24,
      textAlign: "center",
    },
  };
});

type AccessRedirect =
  | "billing"
  | "contact_admin"
  | "deactivated"
  | "wizard"
  | null;

/** Where a logged-in Keycloak user has to go instead of the editor. */
export function getAccessRedirect(me?: Me | null): AccessRedirect {
  if (!me || me.auth_provider !== "keycloak") {
    return null;
  }
  switch (me.state) {
    case "deactivated":
      return "deactivated";
    case "free_period_expired":
      return me.organizations.some((m) => {
        return m.status === "active" && m.role === "admin";
      })
        ? "billing"
        : "contact_admin";
    case "no_organization":
      return "wizard";
    default:
      return null;
  }
}

export function AccessNotice({
  text,
  title,
}: {
  text: string;
  title?: string;
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  return (
    <Box className={classes.root}>
      {title && <Typography variant="h1">{title}</Typography>}
      <Typography>{text}</Typography>
      <Button
        onClick={() => {
          dispatch(logout());
        }}
        variant="outlined"
      >
        {t("Abmelden")}
      </Button>
    </Box>
  );
}

/**
 * Renders the editor only if the user's organization access allows it,
 * otherwise a notice or a redirect to the wizard or billing page.
 */
function AccessGate({
  children,
  ...props
}: {
  [key: string]: unknown;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const me = useAppSelector((state) => {
    return state.oidc.me;
  });

  switch (getAccessRedirect(me)) {
    case "billing":
      return <Navigate replace to={BILLING_PATHNAME} />;
    case "contact_admin":
      return (
        <AccessNotice
          text={t(
            "Die Testphase deiner Organisation ist abgelaufen. Bitte wende dich an deinen Admin.",
          )}
        />
      );
    case "deactivated":
      return (
        <AccessNotice
          text={t(
            "Dein Zugang zu dieser Organisation ist deaktiviert. Bitte wende dich an deinen Admin.",
          )}
        />
      );
    case "wizard":
      return <Navigate replace to={WIZARD_PATHNAME} />;
    default:
      // Forward the props App passes to its route element
      return (
        <>
          {React.Children.map(children, (child) => {
            return React.isValidElement(child)
              ? React.cloneElement(child, props)
              : child;
          })}
        </>
      );
  }
}

/** Placeholder for the registration wizard. */
export function WizardPage() {
  const { t } = useTranslation();
  const me = useAppSelector((state) => {
    return state.oidc.me;
  });
  if (getAccessRedirect(me) !== "wizard") {
    return <Navigate replace to="/" />;
  }
  return (
    <AccessNotice
      text={t("Die Einrichtung einer Organisation ist bald verfügbar.")}
      title={t("Organisation einrichten")}
    />
  );
}

/** Placeholder for the billing page. */
export function BillingPage() {
  const { t } = useTranslation();
  const me = useAppSelector((state) => {
    return state.oidc.me;
  });
  if (getAccessRedirect(me) !== "billing") {
    return <Navigate replace to="/" />;
  }
  return (
    <AccessNotice
      text={t(
        "Die Testphase deiner Organisation ist abgelaufen. Wähle einen Plan, um weiterzuarbeiten.",
      )}
      title={t("Upgrade")}
    />
  );
}

export default React.memo(AccessGate);
